import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch, FiShare2, FiX } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { APP_NAME, LOGO_ICON_SRC } from '../../config/branding';
import { storageBoxApi } from '../api/storageBoxApi';
import { STORAGE_BOX_ROOT_LABEL } from '../constants';

function formatShareTarget(row) {
  if (row.kind === 'link') return row.linkLabel || 'Anyone with the link';
  return row.granteeName || row.granteeEmail || row.granteeUserId || 'User';
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return 'No expiry';
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function normalizeQuery(q) {
  return String(q || '').trim().toLowerCase();
}

function rowMatchesWithMe(row, q) {
  if (!q) return true;
  const hay = [row.name, row.ownerName, row.ownerUserId, row.type]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function rowMatchesByMe(row, q) {
  if (!q) return true;
  const hay = [
    row.itemName,
    row.itemType,
    formatShareTarget(row),
    row.granteeEmail,
    row.granteeUserId,
    row.kind === 'link' ? 'link' : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export default function StorageBoxSharedManageModal({
  isOpen,
  onClose,
  scope,
  onNotify,
  activeSharedShareId,
  onAfterChange,
}) {
  const lockScope = scope === 'lock';
  const scopeLabel = lockScope ? 'Lock folder' : STORAGE_BOX_ROOT_LABEL;
  const [tab, setTab] = useState('withMe');
  const [withMe, setWithMe] = useState([]);
  const [byMe, setByMe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filterLock = useCallback((row) => Boolean(row.requiresLockPin) === lockScope, [lockScope]);

  const load = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    Promise.all([storageBoxApi.listSharedWithMe(), storageBoxApi.listSharedByMe()])
      .then(([withRes, byRes]) => {
        setWithMe((withRes.shares || []).filter(filterLock));
        setByMe((byRes.shares || []).filter(filterLock));
      })
      .catch(() => {
        setWithMe([]);
        setByMe([]);
        onNotify?.('Could not load sharing details');
      })
      .finally(() => setLoading(false));
  }, [isOpen, filterLock, onNotify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isOpen) return;
    setTab('withMe');
    setSearchQuery('');
  }, [isOpen, scope]);

  const searchNorm = normalizeQuery(searchQuery);

  const filteredWithMe = useMemo(
    () => withMe.filter((row) => rowMatchesWithMe(row, searchNorm)),
    [withMe, searchNorm]
  );

  const filteredByMe = useMemo(
    () => byMe.filter((row) => rowMatchesByMe(row, searchNorm)),
    [byMe, searchNorm]
  );

  const activeList = tab === 'withMe' ? filteredWithMe : filteredByMe;
  const searchPlaceholder =
    tab === 'withMe'
      ? 'Search by item or who shared it…'
      : 'Search by item, person, or link…';

  const handleDismissIncoming = async (shareId) => {
    setBusyId(shareId);
    try {
      await storageBoxApi.dismissShareAsGrantee(shareId);
      onNotify?.('Removed from your shared list');
      window.dispatchEvent(new CustomEvent('storagebox-shared-list-changed'));
      if (activeSharedShareId === shareId) {
        onAfterChange?.({ clearedShareId: shareId });
      }
      load();
    } catch (e) {
      onNotify?.(e.message || 'Could not remove share');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeOutgoing = async (shareId) => {
    setBusyId(shareId);
    try {
      await storageBoxApi.revokeShare(shareId);
      onNotify?.('Share revoked');
      window.dispatchEvent(new CustomEvent('storagebox-shared-list-changed'));
      load();
    } catch (e) {
      onNotify?.(e.message || 'Could not revoke share');
    } finally {
      setBusyId(null);
    }
  };

  const tabBtnClass = (id) =>
    id === tab ? 'sb-share-tabs__btn sb-share-tabs__btn--active' : 'sb-share-tabs__btn';

  return (
    <CenterModalWindow
      isOpen={isOpen}
      onClose={onClose}
      width="min(520px, calc(100vw - 32px))"
      height="auto"
      contentStyle={{ height: 'auto', maxHeight: 'min(85vh, 720px)' }}
      contentClassName="sb-shared-manage-modal"
    >
      <div className="sb-shared-manage-modal__body" data-modal-scroll-port>
        <header className="sb-shared-manage-modal__head">
          <span className="sb-shared-manage-modal__icon" aria-hidden>
            <img src={LOGO_ICON_SRC} alt="" className="sb-shared-manage-modal__logo" />
          </span>
          <div className="sb-shared-manage-modal__head-text">
            <p className="sb-shared-manage-modal__kicker">{APP_NAME} · {scopeLabel}</p>
            <h2 className="sb-shared-manage-modal__title" id="sb-shared-manage-title">
              Manage sharing
            </h2>
          </div>
        </header>

        <p className="sb-shared-manage-modal__hint">
          Review shares in {scopeLabel}. Use the tabs to switch between incoming and outgoing access.
        </p>

        <div className="sb-share-tabs sb-shared-manage-modal__tabs" role="tablist" aria-labelledby="sb-shared-manage-title">
          <button
            type="button"
            role="tab"
            id="sb-shared-tab-with-me"
            aria-selected={tab === 'withMe'}
            aria-controls="sb-shared-panel-with-me"
            className={tabBtnClass('withMe')}
            onClick={() => setTab('withMe')}
          >
            Shared with me
            {!loading ? <span className="sb-shared-manage-modal__tab-count">{withMe.length}</span> : null}
          </button>
          <button
            type="button"
            role="tab"
            id="sb-shared-tab-by-me"
            aria-selected={tab === 'byMe'}
            aria-controls="sb-shared-panel-by-me"
            className={tabBtnClass('byMe')}
            onClick={() => setTab('byMe')}
          >
            Shared by me
            {!loading ? <span className="sb-shared-manage-modal__tab-count">{byMe.length}</span> : null}
          </button>
        </div>

        <div className="sb-drive-search-wrap sb-drive-search-wrap--brand sb-drive-search-wrap--compact sb-shared-manage-modal__search">
          <FiSearch className="sb-drive-search-icon" aria-hidden />
          <input
            type="search"
            role="searchbox"
            className="sb-drive-search"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={searchPlaceholder}
            autoComplete="off"
          />
          {searchQuery ? (
            <button
              type="button"
              className="sb-drive-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <FiX />
            </button>
          ) : (
            <FiShare2 className="sb-shared-manage-modal__search-accent" aria-hidden />
          )}
        </div>

        {loading ? <p className="sb-drive-sidebar-section__hint">Loading…</p> : null}

        {!loading && tab === 'withMe' ? (
          <div
            role="tabpanel"
            id="sb-shared-panel-with-me"
            aria-labelledby="sb-shared-tab-with-me"
            className="sb-shared-manage-modal__panel"
          >
            <ul className="sb-shared-manage-modal__list">
              {!filteredWithMe.length ? (
                <li className="sb-shared-manage-modal__empty">
                  {searchNorm ? 'No matches for your search.' : 'Nothing shared with you here yet.'}
                </li>
              ) : (
                filteredWithMe.map((row) => (
                  <li key={row.shareId} className="sb-shared-manage-modal__row">
                    <div className="sb-shared-manage-modal__row-main">
                      <span className="sb-shared-manage-modal__name">{row.name}</span>
                      <span className="sb-shared-manage-modal__meta">From {row.ownerName || 'Unknown'}</span>
                    </div>
                    <button
                      type="button"
                      className="sb-revoke-share-btn"
                      disabled={busyId === row.shareId}
                      onClick={() => handleDismissIncoming(row.shareId)}
                    >
                      {busyId === row.shareId ? '…' : 'Remove access'}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}

        {!loading && tab === 'byMe' ? (
          <div
            role="tabpanel"
            id="sb-shared-panel-by-me"
            aria-labelledby="sb-shared-tab-by-me"
            className="sb-shared-manage-modal__panel"
          >
            <p className="sb-shared-manage-modal__subhint">Shares stay active until you revoke access below.</p>
            <ul className="sb-shared-manage-modal__list">
              {!filteredByMe.length ? (
                <li className="sb-shared-manage-modal__empty">
                  {searchNorm ? 'No matches for your search.' : 'You have not shared anything here yet.'}
                </li>
              ) : (
                filteredByMe.map((row) => (
                  <li key={row.shareId} className="sb-shared-manage-modal__row">
                    <div className="sb-shared-manage-modal__row-main">
                      <span className="sb-shared-manage-modal__name">{row.itemName}</span>
                      <span className="sb-shared-manage-modal__meta">
                        To {formatShareTarget(row)} · {formatExpiry(row.expiresAt)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="sb-revoke-share-btn"
                      disabled={busyId === row.shareId}
                      onClick={() => handleRevokeOutgoing(row.shareId)}
                    >
                      {busyId === row.shareId ? '…' : 'Revoke'}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}

        {!loading && searchNorm && activeList.length > 0 ? (
          <p className="sb-shared-manage-modal__result-count">
            Showing {activeList.length} result{activeList.length === 1 ? '' : 's'}
          </p>
        ) : null}

        <div className="sb-name-modal__actions sb-shared-manage-modal__actions">
          <button type="button" className="sb-primary-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </CenterModalWindow>
  );
}
